import { Field, IconButton, Modal, Text, TextInput } from "@carbon6/c6-frontend-design";
import { useState, type ChangeEvent, type ReactNode, type SyntheticEvent } from "react";
import { SESSION_DISPLAY_NAME_MAX_LENGTH } from "@/api";
import { hatcheryTextModes } from "@/app/theme";
import { HatModalHeader } from "@/components/modal";
import styles from "./SessionNameDialog.module.css";

type SessionNameDialogProps = {
  // 모달 제목·안내문·제출 버튼 문구. 동작(이어서 학습/복제/이름 변경)의 뜻은 문구로 구분한다.
  title: string;
  hint: string;
  submitLabel: string;
  pendingLabel: string;
  // 입력 칸 라벨(선택, 기본 "새 세션 이름"). 이름을 고치는 경우에는 새로 만드는 이름이 아니라
  // 그 세션의 이름이므로 라벨이 다르다.
  fieldLabel?: string;
  // 부모 이름에서 파생한 기본값(derivedSessionDefaultName) 또는 현재 이름. 사용자가 고칠 수 있다.
  defaultName: string;
  submitting: boolean;
  errorMessage: string | null;
  onClose: () => void;
  onSubmit: (displayName: string) => void;
};

// 세션 이름을 받는 확인 모달. 새 세션을 만드는 동작(이어서 학습·복제)과 기존 세션의 이름
// 변경이 함께 쓴다.
//
// 만들 때 이름을 묻는 이유가 있다. 생성 경로는 본문에 이름을 포함하지 않아서 새 세션 이름이
// `sess-{uuid8}` 이 된다. 연구자들은 이름으로 세션 체인을 관리한다. 그래서 여기서 이름을 받고,
// 만든 뒤 rename 으로 채운다.
export function SessionNameDialog({
  title,
  hint,
  submitLabel,
  pendingLabel,
  fieldLabel = "새 Session 이름",
  defaultName,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: SessionNameDialogProps): ReactNode {
  const [displayName, setDisplayName] = useState(defaultName);

  function handleSubmit(event: SyntheticEvent<HTMLFormElement, SubmitEvent>): void {
    event.preventDefault();
    if (submitting || displayName.trim() === "") {
      return;
    }
    onSubmit(displayName.trim());
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      closeOnBackdropClick={!submitting}
      closeOnEscape={!submitting}
      aria-labelledby="session-name-dialog-title"
    >
      <HatModalHeader
        title={title}
        titleId="session-name-dialog-title"
        close={{ disabled: submitting }}
      />
      <form className={styles.dialogForm} onSubmit={handleSubmit}>
        <Text variant={hatcheryTextModes["body-normal"]} className={styles.hint}>
          {hint}
        </Text>
        <Field label={fieldLabel} layout="stack" disabled={submitting}>
          <TextInput
            value={displayName}
            maxLength={SESSION_DISPLAY_NAME_MAX_LENGTH}
            required
            placeholder={`${fieldLabel}을 입력해 주세요.`}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setDisplayName(event.target.value)}
          />
        </Field>
        {errorMessage ? (
          <Text variant={hatcheryTextModes["body-normal"]} className={styles.error} role="alert">
            {errorMessage}
          </Text>
        ) : null}
        <div className={styles.actions}>
          <IconButton
            text="취소"
            type="button"
            variant="surface"
            disabled={submitting}
            onClick={onClose}
          />
          <IconButton
            text={submitting ? pendingLabel : submitLabel}
            type="submit"
            variant="solid"
            disabled={submitting || displayName.trim() === ""}
          />
        </div>
      </form>
    </Modal>
  );
}
